import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import useTenantVocabulary, { vocabList } from '../hooks/useTenantVocabulary';
import './ContactsTab.css';

// P0.21 task 21.6c. The default was 'Carrier'; it feeds contacts/create.js,
// which stores it as the contact's RELATIONSHIP (21.6a). 'Customer' is the
// relationship shape, not an industry.
// R3 — TWO MODES, ONE MODAL.
//
// Default (accountId given): the account's Contacts tab, as before — a list plus
// the view/edit/add modal.
//
// Standalone (`contactId` given, no list): renders ONLY the modal, for one
// contact, and calls `onClose` when dismissed. Added so ContactsPage can open a
// contact that has no account at all. Before this, the modal was reachable only
// through an account, so an unlinked contact could not be viewed or edited by any
// route — which is what stranded 1,164 LinkedIn-imported contacts for one tenant.
//
// WHY NOT EXTRACT THE MODAL INTO ITS OWN COMPONENT. That is the tidier shape and
// it is a ~360-line move touching 14 pieces of parent state (selectedContact,
// modalMode, formData, enrichedIds, six handlers…). Doing it in the same change
// that fixes a live user's problem would mean the refactor and the fix fail or
// pass together. The modal stays put; the component learns to render it alone.
// Extraction stays worth doing — see tasks/backlog.md R3 — but on its own.
// How many account links to show before "Click for More". Three, the owner's
// call: enough that the common cases (one account, or a consultant with two or
// three) never need a click, and few enough that a contact linked to a dozen
// does not push the rest of the form off the screen.
const ACCOUNTS_SHOWN = 3;

function ContactsTab({
  accountId,
  accountName = 'Account',
  accountType = 'Customer',
  contactId = null,          // standalone: open just this contact
  onClose = null,            // standalone: called when the modal closes
}) {
  const standalone = Boolean(contactId);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [modalMode, setModalMode] = useState('view');
  const [formData, setFormData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  // A FAILED enrich, beside the success toast rather than swallowed (148.4).
  // Separate state so a failure cannot be mistaken for a success by reusing
  // one channel with different text.
  const [errorMessage, setErrorMessage] = useState('');
  const [linking,       setLinking]       = useState(false); // create-and-link in flight
  // "Click for More" on the accounts list. Reset whenever a different contact is
  // opened, below — otherwise expanding a contact with five accounts would leave
  // the next one expanded too, and the next one may have two.
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  // The "link an existing contact" search in Add mode.
  const [existingQuery,     setExistingQuery]     = useState('');
  const [existingMatches,   setExistingMatches]   = useState([]);
  const [existingSearching, setExistingSearching] = useState(false);
  // Existing accounts whose name resembles what is typed in the Company field.
  // Suggested, never applied automatically — see the search effect below.
  const [acctMatches,   setAcctMatches]   = useState([]);
  const [acctSearching, setAcctSearching] = useState(false);
  const [enrichingId,   setEnrichingId]   = useState(null);  // contact id being ZI enriched
  const [enrichedIds,   setEnrichedIds]   = useState({});    // contactId → enriched data

  // ── Field options ──
  // Note: title is now a free-text input. seniority/function/practice_area/focus
  // are AI-classified by /api/contacts/classify on save (Titles workstream, May 2026).
  // The legacy TITLES, CONTACT_TYPES, DEPARTMENTS, PRACTICE_FOCUS_OPTIONS dropdowns
  // were retired in this deploy.

  const INFLUENCE_LEVELS = [
    'Decision Maker', 'Economic Buyer', 'Champion', 'Coach', 'Sponsor',
    'Influencer', 'Technical Evaluator', 'End User', 'Gatekeeper', 'Blocker',
    'Legal/Procurement', 'Board Member', 'Mobilizer', 'Skeptic', 'Neutralist',
    'Internal Advocate',
  ];

  const RELATIONSHIPS = ['Strong  Relationship', 'Customer', 'Engaged Positve', 'None', 'Negative'];

  // SERVICE LINES COME FROM THE TENANT, not from insurance.
  //
  // This was hardcoded: 'AI & Analytics, Guidewire, Duck Creek, Claims
  // Operations, Legacy Modernization, InsuranceNow, Data & Reporting, Majesco'.
  // Four of those eight are insurance policy-administration VENDORS, offered to
  // every tenant as though they were that tenant's own practice areas. A
  // logistics or professional-services user opening a contact was asked to tag
  // them with Guidewire.
  //
  // Same rule as exavalu_relevance (migration 0030): nothing in the base may
  // carry one customer's market. The tenant already writes its real service lines
  // in Profile & Settings — tenants.settings.commercial_profile.service_lines,
  // which derive-account-attributes.js has been reading all along. The editor was
  // the one place that ignored them.
  //
  // The fallback is deliberately GENERIC and small: it is what renders for the
  // ~200ms before the request answers, and for a tenant that has not filled in
  // its profile. It must not be anybody's industry.
  const FALLBACK_SERVICE_LINES = ['AI & Analytics', 'Data & Reporting', 'Consulting'];
  const tenantVocab = useTenantVocabulary();
  // An EMPTY derived list is honoured; only "not answered yet" falls back. A
  // tenant that has configured no service lines should not be offered another
  // industry's — the fallback exists for the ~200ms before the request answers.
  const SERVICE_LINE_OPTIONS = vocabList(tenantVocab, 'serviceLines', FALLBACK_SERVICE_LINES);

  // Influence level icons and colors
  const INFLUENCE_CONFIG = {
    'Decision Maker':    { icon: '⭐', color: 'var(--dm-red)' },
    'Economic Buyer':    { icon: '💰', color: '#DB2777' },
    'Champion':          { icon: '🏆', color: 'var(--dm-indigo)' },
    'Coach':             { icon: '👤', color: '#EAB308' },
    'Sponsor':           { icon: '🎯', color: 'var(--dm-green-soft)' },
    'Influencer':        { icon: '💼', color: '#A855F7' },
    'Technical Evaluator': { icon: '🔬', color: 'var(--dm-grey-600)' },
    'End User':          { icon: '👥', color: 'var(--dm-grey-600)' },
    'Gatekeeper':        { icon: '🔑', color: '#F97316' },
    'Blocker':           { icon: '🚫', color: '#A855F7' },
    'Legal/Procurement': { icon: '📋', color: 'var(--dm-green-soft)' },
    'Board Member':      { icon: '🏛️', color: '#F97316' },
    'Mobilizer':         { icon: '⚡', color: 'var(--dm-indigo)' },
    'Skeptic':           { icon: '🤨', color: 'var(--dm-red)' },
    'Neutralist':        { icon: '😐', color: 'var(--dm-grey-600)' },
    'Internal Advocate': { icon: '📣', color: 'var(--dm-green-soft)' },
  };

  const getInfluenceConfig = (level) =>
    INFLUENCE_CONFIG[level] || { icon: '👤', color: 'var(--dm-grey-600)' };

  const handleEnrich = async (e, contact) => {
    e.stopPropagation();
    if (!contact.id || enrichingId === contact.id || enrichedIds[contact.id]) return;
    setEnrichingId(contact.id);
    try {
      const res  = await apiFetch('/api/contacts/enrich', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contactId: contact.id }),
      });
      const data = await res.json().catch(() => ({}));
      // res.ok, not just data.success. apiFetch RESOLVES on 4xx/5xx, so the
      // catch below never fires for an HTTP error and `data.success` is merely
      // undefined — neither branch ran, the spinner cleared, and nothing told
      // the user anything. A 404 looked exactly like "no new data found".
      if (!res.ok || data.success === false) {
        setErrorMessage(`⚠️ Enrich failed for ${contact.name}: ${data.error || `HTTP ${res.status}`}`);
        setTimeout(() => setErrorMessage(''), 6000);
        return;
      }
      if (data.success && data.fieldsAdded > 0) {
        setEnrichedIds(prev => ({ ...prev, [contact.id]: data.enriched }));
        setSuccessMessage('✅ ZoomInfo enriched ' + contact.name + ' — ' + data.fieldsAdded + ' field(s) added');
        setTimeout(() => setSuccessMessage(''), 3000);
        await fetchContacts(); // refresh to show new data
      } else if (data.success) {
        setSuccessMessage('ℹ️ No new data found in ZoomInfo for ' + contact.name);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      setErrorMessage(`⚠️ Enrich failed for ${contact.name}: ${err.message}`);
      setTimeout(() => setErrorMessage(''), 6000);
    }
    finally { setEnrichingId(null); }
  };

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      // R3 — standalone fetches the FULL contact by id rather than reusing the
      // caller's object. /api/contacts/search returns 12 fields; this modal's
      // handleSave sends all ten of its properties unconditionally, so seeding
      // the form from a search result would write '' over notes and mobile_phone.
      // One extra round trip is the price of not silently blanking columns.
      const url = standalone
        ? `/api/contacts/get?id=${encodeURIComponent(contactId)}`
        : `/api/contacts/by-account?accountId=${accountId}`;
      const response = await apiFetch(url);
      const data = await response.json();

      // res.ok, not data.success alone — apiFetch resolves on 4xx/5xx, so a 404
      // here would otherwise leave the modal open and empty with nothing said.
      if (!response.ok || data.success === false) {
        setError(data.error || `Could not load (HTTP ${response.status})`);
        return;
      }

      if (standalone) {
        setContacts(data.contact ? [data.contact] : []);
        setSelectedContact(data.contact || null);
        setModalMode('view');
      } else {
        setContacts(data.contacts);
      }
    } catch (err) {
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [accountId, contactId, standalone]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const handleCardClick = (contact) => {
    setSelectedContact(contact);
    setModalMode('view');
  };

  const handleCloseModal = () => {
    setSelectedContact(null);
    setModalMode('view');
    setFormData({});
    setSuccessMessage('');
    // R3 — in standalone there is no list to fall back to, so closing the modal
    // must tell the parent to unmount this component. Without it the modal shuts
    // and leaves an empty container behind, which reads as the same dead click
    // this change exists to fix.
    if (standalone && onClose) onClose();
  };

  const blankForm = () => ({
    name: '',
    title: '',
    company_name: '',
    influence_level: [],
    relationship: [],
    service_lines: [],
    email: '',
    phone: '',
    mobile_phone: '',
    linkedin: '',
    notes: '',
  });

  const handleAddClick = () => {
    setSelectedContact(null);
    setFormData(blankForm());
    setModalMode('add');
  };

  const handleEditClick = (contact) => {
    // Normalise influence_level / relationship to arrays
    const toArray = (v) => {
      if (!v) return [];
      if (Array.isArray(v)) return v;
      return v.split(',').map(s => s.trim()).filter(Boolean);
    };
    setFormData({
      name:           contact.name          || '',
      title:          contact.title         || '',
      influence_level: toArray(contact.influence_level),
      relationship:   toArray(contact.relationship),
      service_lines:  toArray(contact.service_lines),
      email:          contact.email         || '',
      phone:          contact.phone         || '',
      mobile_phone:   contact.mobile_phone  || '',
      linkedin:       contact.linkedin      || '',
      notes:          contact.notes         || '',
      // The stated employer. Editable so a misspelt company — the usual reason a
      // link failed at import — can be corrected and linked without re-uploading.
      company_name:   contact.company_name  || '',
    });
    setModalMode('edit');
  };

  // ── Link this contact to an account, creating the account if it does not exist ──
  //
  // WHY CREATE-ON-THE-FLY RATHER THAN A PICKER OVER EXISTING ACCOUNTS.
  // The state this exists to repair is "the contact arrived with an employer that
  // matched no account". A picker of existing accounts cannot resolve that by
  // definition — the whole point is that the right account is not there yet. So the
  // user would have to leave, create the account, come back and find the contact
  // again, which is the ordering trap that made a 1,164-row import a three-step
  // procedure. One button does it in place.
  //
  // THIS IS STILL A DELIBERATE ACT, WHICH IS THE PART THAT MATTERS.
  // api/research/exec-search.js refuses to auto-create accounts for a reason worth
  // preserving: "contact creation shouldn't have account creation as a side
  // effect". It is not a side effect here — a person reads the name and presses a
  // button. That is exactly the human judgement docs/CONTACTS-MODEL.md argues an
  // account represents: a company you have DECIDED to sell to, not merely somebody's
  // employer. Nothing on this path creates an account without a click.
  //
  // accounts/create is safe to call for a name that already exists: it answers 200
  // with alreadyExists:true and the existing id, so a near-duplicate press links
  // rather than creating a second account.
  // ── Suggest existing accounts for what is typed in the Company field ─────────
  //
  // WHY SUGGEST RATHER THAN MATCH. The import resolves accounts by EXACT name and
  // deliberately does no fuzzy matching (import-validate.js:215: the repo's fuzzy
  // matcher has never matched anything, and a wrong-account match is worse than a
  // rejected row because nothing about it looks wrong later). That reasoning holds
  // for anything AUTOMATIC. It does not hold for a list a person reads: "Majesco"
  // typed against an existing "Majesco Inc." is obvious to a human and invisible to
  // an exact match, and picking from a list is the judgement the import cannot make.
  //
  // So this is the middle path between the two failures: creating a duplicate
  // account beside one that already exists, and silently attaching a contact to the
  // wrong company. The user sees what exists and chooses.
  //
  // Substring search via /api/accounts/search, which is already tenant-scoped and
  // excludes soft-deleted rows. No new endpoint.
  useEffect(() => {
    const q = (formData.company_name || '').trim();
    // Two characters is where a substring search stops returning most of the book.
    if (modalMode !== 'edit' || selectedContact?.accountId || q.length < 2) {
      setAcctMatches([]);
      return;
    }
    let cancelled = false;
    // Debounced: this fires per keystroke otherwise, and the last response to
    // arrive would not necessarily be the last one requested.
    const t = setTimeout(async () => {
      setAcctSearching(true);
      try {
        const res = await apiFetch('/api/accounts/search?limit=8&q=' + encodeURIComponent(q));
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setAcctMatches(res.ok && data.success ? (data.accounts || []) : []);
      } catch {
        // A failed suggestion is not worth a toast: the Create path still works,
        // and an error here would read as though linking itself had failed.
        if (!cancelled) setAcctMatches([]);
      } finally {
        if (!cancelled) setAcctSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [formData.company_name, modalMode, selectedContact]);

  // Existing contacts matching what is typed in the Add modal's search.
  //
  // Mirrors the account-suggestion effect above deliberately — same 250ms debounce,
  // same two-character floor, same cancelled flag, and the same rule that a failed
  // lookup sets no error: the create-a-new-contact path below still works without
  // suggestions, and a toast here would read as though adding had failed.
  useEffect(() => {
    const q = existingQuery.trim();
    if (modalMode !== 'add' || !accountId || q.length < 2) {
      setExistingMatches([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setExistingSearching(true);
      try {
        const res = await apiFetch('/api/contacts/search?mode=name&limit=8&q=' + encodeURIComponent(q));
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setExistingMatches(res.ok && data.success ? (data.contacts || []) : []);
      } catch {
        if (!cancelled) setExistingMatches([]);
      } finally {
        if (!cancelled) setExistingSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [existingQuery, modalMode, accountId]);

  // Link a contact that ALREADY EXISTS to this account.
  //
  // The whole point of the junction: this ADDS a link rather than moving the
  // contact. Before contact_accounts the only way to attach someone was to set
  // contacts.account_id, which would have taken them off whatever account they
  // were on — silently, and with no way back except remembering what it was.
  //
  // Sends `accountId` because contacts.account_id is still the authoritative
  // primary link, and update.js upserts the junction alongside it (step 2).
  // A contact that already has a primary keeps it: we only set account_id when
  // there is none, so linking a consultant to a second client does not re-point
  // their primary.
  const handleLinkExisting = async (match) => {
    if (linking || !match?.id || !accountId) return;
    setLinking(true);
    setErrorMessage('');
    try {
      const properties = match.accountId
        // Already has a primary elsewhere: add the junction row only. update.js
        // upserts from account_id, so this sends the link explicitly instead.
        ? { linkAccountId: accountId }
        // No account at all: this becomes the primary, and the junction row
        // follows from update.js's upsert.
        : { accountId };
      const res = await apiFetch('/api/contacts/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: match.id, properties }),
      });
      // res.ok, not data.success alone — apiFetch resolves on 4xx/5xx.
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        setErrorMessage(`⚠️ Could not link ${match.name}: ${data.error || `HTTP ${res.status}`}`);
        return;
      }
      setSuccessMessage(`✅ Linked ${match.name} to this account`);
      setExistingQuery('');
      setExistingMatches([]);
      await fetchContacts();
      handleCloseModal();
    } catch (err) {
      setErrorMessage(`⚠️ Could not link ${match.name}: ${err.message}`);
    } finally {
      setLinking(false);
    }
  };

  // Disconnect a contact from its account. The inverse of the two link handlers
  // below, and until now the missing half: a contact could be linked and never
  // unlinked, so a mis-link was permanent.
  //
  // api/contacts/update.js has accepted this since it was written — `accountId:
  // null` is documented there as "a legitimate edit, and refusing it would make
  // the field one-way". The gap was only ever in the UI.
  //
  // WHY company_name IS WRITTEN HERE, and why that is the load-bearing part.
  // Migration 0032 added contacts.company_name so an unlinked contact states who
  // it works for instead of becoming anonymous, and the detail panel above renders
  // exactly that ("Stated employer: …"). The two LINK handlers already set it. But
  // five of the six paths that INSERT a contact do not — create.js,
  // ingest-commit.js, enrich.js, process-intel.js and commit-intel.js all write a
  // contact that is already linked, with no company_name — so measured on 16 Oct
  // 2026, all 44 contacts across both fixtures had it empty while all 44 were
  // linked. Disconnecting any of them would have stranded a person with no record
  // of their employer: the precise failure 0032 exists to prevent.
  //
  // Capturing it at the moment of the unlink fixes that for every contact however
  // it was created, which is why it belongs here rather than in six insert paths.
  // An existing non-empty value is NOT overwritten: the user may have typed a more
  // precise employer than the account's name.
  // Takes WHICH link to remove, since a contact may hold several (migration
  // 0035). Called with no arguments it falls back to the primary, which is what
  // every caller did before the accounts list existed.
  const handleDisconnectAccount = async (targetId, targetName) => {
    if (linking || !selectedContact?.accountId) return;
    const linkId = targetId || selectedContact.accountId;
    // The CONTACT's own account name first, the caller's context second.
    //
    // This ordering matters more here than in the field above, because
    // linkedName is what gets WRITTEN into company_name below. With the prop
    // first, a disconnect from the standalone Contacts page would have stored
    // the literal string "Account" as the contact's stated employer — the prop's
    // default — permanently, and the confirm box would have read "Disconnect
    // Alexander Drakoln from Account?".
    const linkedName = targetName || selectedContact.accountName || accountName || '';
    // Confirm, and say what this does NOT do. Deletion is a different act
    // (api/contacts/delete.js, soft via deleted_at) and the two must not be
    // confusable in a dialog.
    // Is this the PRIMARY link, or one of the others? The consequence differs,
    // so the question the user is asked differs too. Removing a secondary link
    // leaves the contact attached to its primary account and it keeps appearing
    // in that account's research — saying "it moves to the Unlinked filter"
    // would be simply false.
    const isPrimaryLink = linkId === selectedContact.accountId;
    const remaining = (selectedContact.accounts || []).filter((a) => a.id !== linkId);

    const ok = window.confirm(
      `Disconnect ${selectedContact.full_name || 'this contact'}`
      + `${linkedName ? ` from ${linkedName}` : ''}?\n\n`
      + 'The contact stays in your list and nothing is deleted. '
      + (isPrimaryLink && !remaining.length
        ? 'It moves to the Unlinked filter, and will not appear in research, '
          + 'signals or outreach sequences until it is linked to an account again.'
        : `It stays linked to ${remaining.length === 1
            ? (remaining[0].name || 'its other account')
            : `its other ${remaining.length} accounts`}`
          + ', and only stops appearing in this one.')
    );
    if (!ok) return;
    setLinking(true);
    setErrorMessage('');
    try {
      // WHAT GETS WRITTEN depends on which link this is.
      //
      // contacts.account_id is still the authoritative PRIMARY link — step 4 of
      // the plan drops it, and until then 33 readers depend on it. So:
      //   - removing a SECONDARY link must not touch account_id at all. Nulling
      //     it would unlink the contact from an account the user did not choose,
      //     which is the most destructive thing this screen could do quietly.
      //   - removing the PRIMARY promotes the next remaining link into it, so
      //     the contact is not orphaned while other links still exist.
      //   - removing the last link nulls it, as before.
      const properties = {};
      if (isPrimaryLink) {
        properties.accountId = remaining.length ? remaining[0].id : null;
      }
      properties.disconnectAccountId = linkId;     // the junction row to remove
      // The stated employer, captured only when the contact is about to have no
      // account at all. With links remaining there is still an account naming
      // this person's employer, so there is nothing to preserve.
      if (isPrimaryLink && !remaining.length
          && !(selectedContact.company_name || '').trim() && linkedName) {
        properties.company_name = linkedName;
      }
      const upd = await apiFetch('/api/contacts/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedContact.id, properties }),
      });
      // res.ok, not data.success alone — apiFetch RESOLVES on 4xx/5xx, so reading
      // only the body leaves a refused unlink looking like it worked, and the row
      // would still be linked after a "success" message.
      const data = await upd.json().catch(() => ({}));
      if (!upd.ok || data.success === false) {
        setErrorMessage(`⚠️ Could not disconnect: ${data.error || `HTTP ${upd.status}`}`);
        return;
      }
      setSuccessMessage(
        linkedName ? `✅ Disconnected from "${linkedName}" — the contact is now unlinked`
                   : '✅ Disconnected — the contact is now unlinked'
      );
      // Update the OPEN MODAL, not only the list behind it.
      //
      // Owner report, 16 Oct 2026: "I am unable to type anything in the Account
      // field of a Contact." Correct, and after a disconnect it was a dead end.
      // fetchContacts() refreshes `contacts`, but in account-tab mode
      // (standalone === false) it only calls setContacts — `selectedContact` is
      // separate state and is left holding the pre-disconnect object. So the row
      // was unlinked in the database while the modal still showed "Account:
      // 609 Padel LLC" with a Disconnect button, and the Company + "Link to
      // account" control never appeared, because it is gated on
      // `!selectedContact?.accountId`. The user had to close and reopen the
      // contact to see the result of their own click.
      //
      // Set from what we KNOW changed rather than re-reading: the handler just
      // wrote these two fields, and the alternative — `contacts.find(...)` as
      // handleSave does — reads a closure captured before the refetch.
      // `accountId` is the only account field contact-shape.js returns, and it
      // is what both branches of the modal switch on. `accountName` is read in
      // one place but has never been in the shape, so it is already undefined
      // here and is not worth inventing.
      setSelectedContact((prev) => (prev ? {
        ...prev,
        accountId:    null,
        company_name: properties.company_name ?? prev.company_name,
      } : prev));
      // The form state behind the modal has to agree, or re-linking starts from
      // a blank Company box when the employer was just captured.
      setFormData((prev) => ({
        ...prev,
        company_name: properties.company_name ?? prev.company_name ?? '',
      }));
      await fetchContacts();
    } catch (err) {
      setErrorMessage(`⚠️ Could not disconnect: ${err.message}`);
    } finally {
      setLinking(false);
    }
  };

  // Link to an account the user PICKED. No creation, and the contact's
  // company_name is set to that account's real name so the hint and the
  // relationship cannot disagree afterwards.
  const handlePickAccount = async (acct) => {
    if (linking) return;
    setLinking(true);
    setErrorMessage('');
    try {
      const upd = await apiFetch('/api/contacts/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selectedContact.id,
          properties: { accountId: acct.id, company_name: acct.accountName },
        }),
      });
      const data = await upd.json().catch(() => ({}));
      if (!upd.ok || data.success === false) {
        setErrorMessage(`⚠️ Could not link: ${data.error || `HTTP ${upd.status}`}`);
        return;
      }
      setSuccessMessage(`✅ Linked to "${acct.accountName}"`);
      setAcctMatches([]);
      await fetchContacts();
    } catch (err) {
      setErrorMessage(`⚠️ Could not link: ${err.message}`);
    } finally {
      setLinking(false);
    }
  };

  const handleLinkToAccount = async () => {
    const name = (formData.company_name || '').trim();
    if (!name || linking) return;
    setLinking(true);
    setErrorMessage('');
    try {
      const res = await apiFetch('/api/accounts/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountName: name }),
      });
      // res.ok, not data.success alone — apiFetch RESOLVES on 4xx/5xx, so checking
      // only the body leaves a failed create looking like a success with no id.
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false || !data.accountId) {
        setErrorMessage(`⚠️ Could not link to "${name}": ${data.error || `HTTP ${res.status}`}`);
        return;
      }

      // Save the link AND the (possibly corrected) company name together, so the
      // two cannot disagree — the hint should read as what was actually linked.
      const upd = await apiFetch('/api/contacts/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selectedContact.id,
          properties: { accountId: data.accountId, company_name: name },
        }),
      });
      const updData = await upd.json().catch(() => ({}));
      if (!upd.ok || updData.success === false) {
        setErrorMessage(`⚠️ Account ready, but linking failed: ${updData.error || `HTTP ${upd.status}`}`);
        return;
      }

      setSuccessMessage(
        data.alreadyExists
          ? `✅ Linked to existing account "${name}"`
          : `✅ Created account "${name}" and linked this contact`
      );
      // Re-read rather than patching local state: the contact now belongs to an
      // account, which changes what the rest of this modal should show.
      await fetchContacts();
    } catch (err) {
      setErrorMessage(`⚠️ Could not link: ${err.message}`);
    } finally {
      setLinking(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleMulti = (field, value) => {
    setFormData(prev => {
      const arr = prev[field] || [];
      return {
        ...prev,
        [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value],
      };
    });
  };

  // Fire-and-forget classification: called after successful save.
  // Does not block UI. On success, refreshes the contact list so the new chips appear.
  // Skips silently if title is empty or classify endpoint fails — failures here
  // shouldn't roll back a successful contact save.
  const classifyContactAfterSave = useCallback(async (contactId, title) => {
    if (!contactId || !title || !title.trim()) return;
    try {
      const res = await apiFetch('/api/contacts/classify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: contactId }),
      });
      const data = await res.json();
      if (data?.success && data?.written) {
        // Refresh so the new seniority/function/practice_area show up in the list
        await fetchContacts();
      }
    } catch (err) {
      // Silent — don't disturb the user. Classification can be retried later.
      console.warn('[contacts] classify failed:', err.message);
    }
  // fetchContacts is defined below this; React's hook rules don't catch this since
  // it's stable across renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    try {
      if (modalMode === 'edit') {
        const titleChanged = formData.title !== (selectedContact?.title || '');
        const response = await apiFetch('/api/contacts/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: selectedContact.id,
            accountType,
            properties: {
              name:           formData.name,
              title:          formData.title,
              influenceLevel: formData.influence_level,
              relationship:   formData.relationship,
              serviceLines:   formData.service_lines,
              email:          formData.email,
              phone:          formData.phone,
              mobile_phone:   formData.mobile_phone,
              linkedin:       formData.linkedin,
              notes:          formData.notes,
            },
          }),
        });
        const data = await response.json();
        if (data.success) {
          setSuccessMessage('✅ Contact updated successfully!');
          await fetchContacts();
          // Re-classify if title changed
          if (titleChanged && formData.title) {
            classifyContactAfterSave(selectedContact.id, formData.title);
          }
          setTimeout(() => {
            setModalMode('view');
            setSuccessMessage('');
            const updated = contacts.find(c => c.id === selectedContact.id);
            if (updated) setSelectedContact({ ...updated, ...formData });
          }, 1500);
        } else {
          alert('Error: ' + data.error);
        }
      } else if (modalMode === 'add') {
        const response = await apiFetch('/api/contacts/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountId,
            accountType,
            properties: {
              name:           formData.name,
              title:          formData.title,
              influence_level: formData.influence_level,
              relationship:   formData.relationship,
              service_lines:  formData.service_lines,
              email:          formData.email,
              phone:          formData.phone,
              mobile_phone:   formData.mobile_phone,
              linkedin:       formData.linkedin,
              notes:          formData.notes,
            },
          }),
        });
        const data = await response.json();
        if (data.success) {
          setSuccessMessage('✅ Contact created successfully!');
          await fetchContacts();
          // Classify new contact if title was provided
          if (data.contactId && formData.title) {
            classifyContactAfterSave(data.contactId, formData.title);
          }
          setFormData(blankForm());
          setTimeout(() => setSuccessMessage(''), 2000);
        } else {
          alert('Error: ' + data.error);
        }
      }
    } catch (err) {
      alert('Error saving: ' + err.message);
    }
  };

  const handleSaveAndClose = async () => {
    await handleSave();
    // Close after a brief moment to allow success message to show
    setTimeout(() => handleCloseModal(), 800);
  };

  const handleDeleteClick = (contact) => {
    setDeletingContact(contact);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await apiFetch('/api/contacts/delete', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: deletingContact.id }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Contact deleted successfully!');
        await fetchContacts();
        setShowDeleteConfirm(false);
        setDeletingContact(null);
        handleCloseModal();
        setTimeout(() => setSuccessMessage(''), 2000);
      } else {
        alert('Error: ' + data.error);
      }
    } catch (err) {
      alert('Error deleting: ' + err.message);
    }
  };

  const handleSendEmail = (contact) => {
    if (!contact.email) { alert('No email address for this contact'); return; }
    const subject = encodeURIComponent(`Following up - ${accountName || 'Your Company'}`);
    const body = encodeURIComponent(`Hi ${contact.name.split(' ')[0]},\n\n`);
    window.location.href = `mailto:${contact.email}?subject=${subject}&body=${body}`;
  };

  const toArray = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) return v;
    return v.split(',').map(s => s.trim()).filter(Boolean);
  };

  // ── R3: the modal, extracted so standalone mode can render it alone ──────
  //
  // Body is UNCHANGED — same state, same handlers, same markup. Only the JSX
  // wrapper moved, from an inline `{cond && (…)}` inside the list render to a
  // function both render paths call. The account Contacts tab renders it after
  // the list; ContactsPage renders it with no list at all.
  function renderModal() {
    if (!(selectedContact || modalMode === 'add')) return null;

    // THE ACCOUNTS THIS CONTACT BELONGS TO, with a fallback that matters.
    //
    // `accounts` is populated only when the caller joined contact_accounts. A
    // caller that did not gets [], which means "not asked" and NOT "belongs to
    // nothing" — contact-shape.js says so at the field. Without the fallback a
    // legacy caller would render a contact with an accountId and no account
    // rows, which reads as data loss.
    //
    // So: use the array when it has anything, otherwise synthesise the single
    // row the UI showed before the junction existed.
    const accountList = (selectedContact?.accounts?.length)
      ? selectedContact.accounts
      : (selectedContact?.accountId
          ? [{ id: selectedContact.accountId,
               name: selectedContact.accountName || accountName || '',
               role: '', isPrimary: false }]
          : []);
    const visibleAccounts = showAllAccounts ? accountList : accountList.slice(0, ACCOUNTS_SHOWN);

    return (
        <div
          className="contact-modal-overlay"
          onClick={modalMode === 'view' ? handleCloseModal : undefined}
        >
          <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
            <div className="contact-modal-header">
              <h3>
                {modalMode === 'add'
                  ? 'Add New Contact'
                  : selectedContact?.name}
                {modalMode === 'view' && selectedContact?.title && ` — ${selectedContact.title}`}
              </h3>
              <button className="close-btn" onClick={handleCloseModal}>×</button>
            </div>

            <div className="contact-modal-content">

              {/* ── VIEW mode ── */}
              {modalMode === 'view' && selectedContact && (
                <>
                  {/* R3.11 — AN UNLINKED CONTACT IS INERT, AND SAYS SO.
                      Product position (1), decided 26 Sep 2026: a contact is
                      subordinate to an account. Research, signals and sequences all
                      run per account (nightly-curate and run-batch iterate accounts;
                      sequence recipients are looked up FROM an account), so a contact
                      with no account is genuinely doing nothing — by design, not by
                      fault. exec-search.js already creates such contacts on purpose.
                      Before this line the state was invisible, so "nothing happens"
                      looked like a bug rather than a missing link. A quiet note, not
                      a warning: it is a normal waiting-room state, not an error. */}
                  {!selectedContact.accountId && (
                    <div className="contact-detail-section contact-unlinked-note">
                      <label>Account</label>
                      <p className="contact-unlinked-text">
                        Not linked to an account.
                        {selectedContact.company_name
                          ? <> Stated employer: <strong>{selectedContact.company_name}</strong>.</>
                          : null}
                        {' '}Research, signals and outreach sequences run per account,
                        so this contact will not appear in them until it is linked.
                        {' '}Use <strong>Edit</strong> to link it.
                      </p>
                    </div>
                  )}

                  {/* Classification — AI-derived fields populated by /api/contacts/classify */}
                  {(selectedContact.seniority || selectedContact.function || selectedContact.practice_area || selectedContact.focus) && (
                    <div className="contact-detail-section">
                      <label>Classification</label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {selectedContact.seniority && (
                          <span className="contact-tag contact-tag--blue">{selectedContact.seniority}</span>
                        )}
                        {selectedContact.function && (
                          <span className="contact-tag contact-tag--blue">{selectedContact.function}</span>
                        )}
                        {selectedContact.practice_area && selectedContact.practice_area !== 'None' && (
                          <span className="contact-tag contact-tag--purple">{selectedContact.practice_area}</span>
                        )}
                        {selectedContact.focus && (
                          <span className="contact-tag contact-tag--green">{selectedContact.focus}</span>
                        )}
                      </div>
                      {selectedContact.classification_source === 'ai' && (
                        <div style={{ fontSize: '11px', color: 'var(--dm-grey-500)', marginTop: '4px' }}>
                          AI-classified · click Edit to override
                        </div>
                      )}
                    </div>
                  )}

                  {/* Legacy department fallback — shown only when no classification exists yet */}
                  {!selectedContact.seniority && !selectedContact.function && !selectedContact.practice_area && selectedContact.department && (
                    <div className="contact-detail-section">
                      <label>Department (legacy)</label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span className="contact-tag contact-tag--purple" style={{ opacity: 0.6 }}>
                          {selectedContact.department}
                        </span>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--dm-grey-500)', marginTop: '4px' }}>
                        Not yet AI-classified · edit title to trigger classification
                      </div>
                    </div>
                  )}

                  {/* Influence Level */}
                  {toArray(selectedContact.influence_level).length > 0 && (
                    <div className="contact-detail-section">
                      <label>Influence Level:</label>
                      <div className="relationships-display">
                        {toArray(selectedContact.influence_level).map((lvl, i) => {
                          const c = getInfluenceConfig(lvl);
                          return (
                            <span
                              key={i}
                              className="influence-badge"
                              style={{ backgroundColor: c.color + '20', color: c.color }}
                            >
                              {c.icon} {lvl}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Relationship */}
                  {toArray(selectedContact.relationship).length > 0 && (
                    <div className="contact-detail-section">
                      <label>Relationship:</label>
                      <div className="relationships-display">
                        {toArray(selectedContact.relationship).map((rel, idx) => (
                          <span
                            key={idx}
                            className={`relationship-badge relationship-${rel.toLowerCase().replace(/ /g, '-')}`}
                          >
                            {rel}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Service Lines */}
                  {toArray(selectedContact.service_lines).length > 0 && (
                    <div className="contact-detail-section">
                      <label>Service Lines:</label>
                      <div className="contact-tags">
                        {toArray(selectedContact.service_lines).map((sl, i) => (
                          <span key={i} className="contact-tag contact-tag--amber">{sl}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Contact Info.
                      The HEADING is unconditional while every item under it is
                      conditional, so a contact with no email, phone, mobile or
                      LinkedIn rendered a label over an empty box. Measured on the
                      LightSpeed tenant, 16 Oct 2026: all 18 contacts had none of
                      the four, so the view step was an empty modal with two
                      buttons — the owner asked whether the step should exist.

                      It should. Click-to-edit would drop every accidental click
                      into a form with a Save button, on a screen whose recent
                      defects were all "the form wrote something I did not
                      intend". What it must not do is look broken.

                      The note names the two actions in this modal's own footer,
                      so it points at buttons the user can see. It says nothing
                      about WHERE the contact came from: every contact missing
                      these four fields today has source 'Selma Research' (18 of
                      18 on one tenant, 21 of 26 on the other), so "found by
                      research" would be true now and false for the first
                      hand-created or CSV-imported contact. And it does not branch
                      on zoominfo_id, which contact-shape.js never returns — copy
                      gated on an always-undefined field is right by accident. */}
                  <div className="contact-detail-section">
                    <label>Contact Information:</label>
                    {!selectedContact.email && !selectedContact.phone
                      && !selectedContact.mobile_phone && !selectedContact.linkedin && (
                      <p className="contact-unlinked-text">
                        No email, phone or LinkedIn recorded yet.
                        {' '}Use <strong>Edit</strong> to add them, or
                        {' '}<strong>ZoomInfo Enrich</strong> to look them up.
                      </p>
                    )}
                    <div className="contact-info-list">
                      {selectedContact.email && (
                        <div className="contact-info-item">
                          <span className="info-icon">📧</span>
                          <a href={`mailto:${selectedContact.email}`}>{selectedContact.email}</a>
                        </div>
                      )}
                      {selectedContact.phone && (
                        <div className="contact-info-item">
                          <span className="info-icon">📞</span>
                          <a href={`tel:${selectedContact.phone}`}>{selectedContact.phone}</a>
                        </div>
                      )}
                      {selectedContact.mobile_phone && (
                        <div className="contact-info-item">
                          <span className="info-icon">📱</span>
                          <a href={`tel:${selectedContact.mobile_phone}`}>{selectedContact.mobile_phone}</a>
                        </div>
                      )}
                      {selectedContact.linkedin && (
                        <div className="contact-info-item">
                          <span className="info-icon">🔗</span>
                          <a href={selectedContact.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn Profile</a>
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedContact.notes && (
                    <div className="contact-detail-section">
                      <label>Notes:</label>
                      <div className="contact-notes">{selectedContact.notes}</div>
                    </div>
                  )}

                  <div className="contact-modal-actions">
                    <button
                      className="btn-send-email"
                      onClick={() => handleSendEmail(selectedContact)}
                      disabled={!selectedContact.email}
                    >
                      📧 Send Email
                    </button>
                    {!selectedContact.zoominfo_id && !enrichedIds[selectedContact.id] && (
                      <button className="ct-enrich-btn ct-enrich-btn--modal"
                        onClick={e => handleEnrich(e, selectedContact)}
                        disabled={enrichingId === selectedContact.id}>
                        {enrichingId === selectedContact.id ? '⏳ Enriching...' : '⚡ ZoomInfo Enrich'}
                      </button>
                    )}
                    {(selectedContact.zoominfo_id || enrichedIds[selectedContact.id]) && (
                      <span className="ct-enriched-badge ct-enriched-badge--modal">✓ ZoomInfo Enriched</span>
                    )}
                    <button className="btn-edit" onClick={() => handleEditClick(selectedContact)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDeleteClick(selectedContact)}>Delete</button>
                  </div>
                </>
              )}

              {/* ── ADD: link someone who already exists ──────────────────
                  The owner's point: "when i go to add, it should give me an
                  option to select from existing contacts (since it is a many to
                  many relationship)."
                  Before the junction this could not be offered honestly —
                  picking an already-linked contact would have MOVED it, taking
                  it off another account silently. Now it ADDS a link, which is
                  the whole point of contact_accounts.
                  It sits above the name field rather than behind a second
                  button, so the search is the first thing offered and typing a
                  new contact is what you do when it finds nothing. That order
                  is what stops duplicates being created by default.
                  Account-page only: standalone mode has no account to link to,
                  and `accountId` is the prop that supplies it. */}
              {modalMode === 'add' && accountId && (
                <div className="contact-form ct-existing-picker">
                  <div className="form-group">
                    <label>Link an existing contact</label>
                    <input
                      type="text"
                      value={existingQuery}
                      onChange={(e) => setExistingQuery(e.target.value)}
                      placeholder="Search contacts by name…"
                    />
                    {existingSearching && (
                      <p className="ct-link-hint">Searching…</p>
                    )}
                    {!existingSearching && existingQuery.trim().length >= 2
                      && existingMatches.length === 0 && (
                      <p className="ct-link-hint">
                        No existing contact matches “{existingQuery.trim()}” — fill in
                        the form below to create a new one.
                      </p>
                    )}
                    {existingMatches.length > 0 && (
                      <ul className="ct-acct-list">
                        {existingMatches.map((m) => {
                          // Already on THIS account? Then there is nothing to add,
                          // and offering the button would produce a no-op the user
                          // would reasonably read as broken.
                          const here = m.accountId === accountId;
                          return (
                            <li key={m.id}>
                              <button
                                type="button"
                                className="ct-acct-option"
                                onClick={() => handleLinkExisting(m)}
                                disabled={linking || here}
                                title={here
                                  ? 'Already linked to this account'
                                  : `Link ${m.name} to this account`}
                              >
                                <span className="ct-acct-name">{m.name}</span>
                                {m.title && <span className="ct-acct-meta">{m.title}</span>}
                                {/* Where they already are. This is the fact that
                                    makes the choice informed: linking a contact
                                    who is at another account ADDS a second link,
                                    it does not move them. */}
                                {m.accountName && (
                                  <span className="ct-acct-meta">
                                    {here ? 'already here' : `at ${m.accountName}`}
                                  </span>
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    <p className="ct-link-hint">
                      Linking keeps the contact on any other accounts it belongs
                      to — it is added here, not moved.
                    </p>
                  </div>
                </div>
              )}

              {/* ── ADD / EDIT form ── */}
              {(modalMode === 'edit' || modalMode === 'add') && (
                <div className="contact-form">

                  {/* Name */}
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder="e.g., Mark Russell"
                    />
                  </div>

                  {/* Account — shown only when the contact IS linked, and the
                      mirror image of the Company block below.
                      Between them the two cover both states: linked contacts get
                      Disconnect, unlinked contacts get Link. Before this, a linked
                      contact had no account control at all, which is how a
                      mis-link became permanent.
                      Deliberately NOT an editable account field: re-pointing a
                      contact at a different account is disconnect-then-link, two
                      decisions, and a dropdown that silently re-links is how the
                      wrong account gets chosen by accident. */}
                  {/* ACCOUNTS — plural since migration 0035.
                      One row per link, each with its own Disconnect, because
                      disconnecting one must not touch the others.
                      `accountList` falls back to a single synthetic row built
                      from accountId/accountName, so a caller that did not join
                      the junction still renders exactly what it did before.
                      That matters: `accounts: []` means "not asked", not
                      "belongs to nothing". */}
                  {modalMode === 'edit' && selectedContact?.accountId && (
                    <div className="form-group">
                      <label>{accountList.length > 1 ? 'Accounts' : 'Account'}</label>
                      {visibleAccounts.map((a) => (
                        <div className="ct-link-row ct-link-row--account" key={a.id}>
                          <input
                            type="text"
                            /* The CONTACT's own account name. `accountName` is a
                               prop defaulting to the literal 'Account', so in
                               standalone mode the default used to win and the
                               field read "Account" for a contact linked to United
                               Medical Doctors. The row knows its account. */
                            value={a.name || accountName || 'Linked'}
                            readOnly
                            disabled
                          />
                          {a.role && <span className="ct-acct-role">{a.role}</span>}
                          {a.isPrimary && <span className="ct-acct-primary" title="Primary contact at this account">★</span>}
                          <button
                            type="button"
                            className="btn-link-account btn-link-account--danger"
                            onClick={() => handleDisconnectAccount(a.id, a.name)}
                            disabled={linking}
                            title={`Remove the link to ${a.name || 'this account'}. The contact is kept.`}
                          >
                            {linking ? 'Disconnecting…' : 'Disconnect'}
                          </button>
                        </div>
                      ))}

                      {/* Up to three, then the rest behind one click — the owner's
                          call. The count is in the label so the user knows what is
                          hidden before deciding to look. */}
                      {accountList.length > ACCOUNTS_SHOWN && (
                        <button
                          type="button"
                          className="ct-acct-more"
                          onClick={() => setShowAllAccounts((v) => !v)}
                        >
                          {showAllAccounts
                            ? 'Show fewer'
                            : `Click for ${accountList.length - ACCOUNTS_SHOWN} more`}
                        </button>
                      )}

                      <p className="ct-link-hint">
                        Disconnect keeps the contact and removes only that one link.
                        It will stop appearing in that account&apos;s research,
                        signals and outreach.
                      </p>
                    </div>
                  )}

                  {/* Company — the stated employer, and the link control.
                      Shown only when the contact has no account: once linked, the
                      account is the truth and this field would invite a second,
                      disagreeing answer. Edit mode only — `add` has no contact id
                      to link yet. */}
                  {modalMode === 'edit' && !selectedContact?.accountId && (
                    <div className="form-group">
                      <label>Company</label>
                      <div className="ct-link-row">
                        <input
                          type="text"
                          value={formData.company_name || ''}
                          onChange={(e) => handleFormChange('company_name', e.target.value)}
                          placeholder="Company name"
                        />
                        <button
                          type="button"
                          className="btn-link-account"
                          onClick={handleLinkToAccount}
                          disabled={linking || !(formData.company_name || '').trim()}
                          title="Create this account if it does not exist, then link this contact to it"
                        >
                          {linking ? 'Linking…' : 'Link to account'}
                        </button>
                      </div>
                      {/* Existing accounts that resemble what was typed. Offered,
                          never applied: an exact-match import cannot tell "Majesco"
                          from an existing "Majesco Inc.", and a person can. Picking
                          one links to it; ignoring them and pressing the button
                          creates a new account. */}
                      {acctMatches.length > 0 && (
                        <div className="ct-acct-matches">
                          <p className="ct-acct-matches-label">
                            {acctMatches.length === 1 ? 'An existing account' : 'Existing accounts'} matching
                            “{(formData.company_name || '').trim()}” — pick one, or create a new one above:
                          </p>
                          <ul className="ct-acct-list">
                            {acctMatches.map(a => (
                              <li key={a.id}>
                                <button
                                  type="button"
                                  className="ct-acct-option"
                                  onClick={() => handlePickAccount(a)}
                                  disabled={linking}
                                >
                                  <span className="ct-acct-name">{a.accountName}</span>
                                  {a.accountType && <span className="ct-acct-meta">{a.accountType}</span>}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <p className="ct-link-hint">
                        {acctSearching
                          ? 'Checking for existing accounts…'
                          : 'Link to account creates the account if it does not already exist.'}
                        {' '}Until a contact is linked, research, signals and outreach
                        sequences skip it.
                      </p>
                    </div>
                  )}

                  {/* Title — free text. AI classifies on save into Seniority/Function/Practice Area/Focus. */}
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={formData.title || ''}
                      onChange={(e) => handleFormChange('title', e.target.value)}
                      placeholder="e.g. VP of Claims, Chief Underwriting Officer, Director of Technology"
                    />
                    <div style={{ fontSize: '11px', color: 'var(--dm-grey-500)', marginTop: '4px' }}>
                      AI will classify seniority, function, and practice area on save.
                    </div>
                  </div>

                  {/* If contact has existing classification, show it read-only here so user sees what AI inferred */}
                  {modalMode === 'edit' && selectedContact && (selectedContact.seniority || selectedContact.function || selectedContact.practice_area || selectedContact.focus) && (
                    <div className="form-group">
                      <label>Current Classification</label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {selectedContact.seniority && (
                          <span className="contact-tag contact-tag--blue">{selectedContact.seniority}</span>
                        )}
                        {selectedContact.function && (
                          <span className="contact-tag contact-tag--blue">{selectedContact.function}</span>
                        )}
                        {selectedContact.practice_area && selectedContact.practice_area !== 'None' && (
                          <span className="contact-tag contact-tag--purple">{selectedContact.practice_area}</span>
                        )}
                        {selectedContact.focus && (
                          <span className="contact-tag contact-tag--green">{selectedContact.focus}</span>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--dm-grey-500)', marginTop: '4px' }}>
                        Will re-classify if you change the title.
                      </div>
                    </div>
                  )}

                  {/* Influence Level — multi-select grid */}
                  <div className="form-group">
                    <label>Influence Level (select all that apply)</label>
                    <div className="checkbox-group--influence">
                      {INFLUENCE_LEVELS.map(lvl => {
                        const c = getInfluenceConfig(lvl);
                        const checked = (formData.influence_level || []).includes(lvl);
                        return (
                          <label
                            key={lvl}
                            className={`checkbox-label--influence${checked ? ' checked' : ''}`}
                            style={checked ? { borderColor: c.color, background: c.color + '10' } : {}}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleMulti('influence_level', lvl)}
                            />
                            {c.icon} {lvl}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Relationship */}
                  <div className="form-group">
                    <label>Relationship (select all that apply)</label>
                    <div className="checkbox-group">
                      {RELATIONSHIPS.map(rel => (
                        <label key={rel} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={(formData.relationship || []).includes(rel)}
                            onChange={() => toggleMulti('relationship', rel)}
                          />
                          {rel}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Service Lines — multi-select */}
                  <div className="form-group">
                    <label>Service Lines (select all that apply)</label>
                    <div className="checkbox-group">
                      {SERVICE_LINE_OPTIONS.map(sl => (
                        <label key={sl} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={(formData.service_lines || []).includes(sl)}
                            onChange={() => toggleMulti('service_lines', sl)}
                          />
                          {sl}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      placeholder="email@company.com"
                    />
                  </div>

                  {/* Phone + Mobile */}
                  <div className="form-row">
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={formData.phone || ''}
                        onChange={(e) => handleFormChange('phone', e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div className="form-group">
                      <label>Mobile Phone</label>
                      <input
                        type="tel"
                        value={formData.mobile_phone || ''}
                        onChange={(e) => handleFormChange('mobile_phone', e.target.value)}
                        placeholder="(555) 987-6543"
                      />
                    </div>
                  </div>

                  {/* LinkedIn */}
                  <div className="form-group">
                    <label>LinkedIn</label>
                    <input
                      type="url"
                      value={formData.linkedin || ''}
                      onChange={(e) => handleFormChange('linkedin', e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>

                  {/* Notes */}
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={formData.notes || ''}
                      onChange={(e) => handleFormChange('notes', e.target.value)}
                      rows={4}
                      placeholder="Additional information about this contact..."
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      className="btn-cancel"
                      onClick={() => modalMode === 'add' ? handleSaveAndClose() : setModalMode('view')}
                    >
                      {modalMode === 'add' ? 'Save & Close' : 'Cancel'}
                    </button>
                    <button className="btn-save" onClick={handleSave}>
                      {modalMode === 'edit' ? 'Save Changes' : 'Create & Add Another'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
    );
  }

  // R3 — same move, same reason. Reachable from the modal's Delete button, so
  // it has to exist in standalone too or Delete opens nothing.
  function renderDeleteConfirm() {
    if (!(showDeleteConfirm)) return null;
    return (
        <div className="contact-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="delete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Contact?</h3>
            <p>Are you sure you want to delete <strong>{deletingContact?.name}</strong>?</p>
            <p className="delete-warning">This action cannot be undone.</p>
            <div className="delete-actions">
              <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="btn-delete-confirm" onClick={handleDeleteConfirm}>Delete</button>
            </div>
          </div>
        </div>
    );
  }

  if (loading) return <div className="contacts-loading">Loading contacts...</div>;
  if (error)   return <div className="contacts-error">Error: {error}</div>;

  // R3 — standalone renders the modal and nothing else. The list, the header and
  // the "+ Add Contact" button all belong to an account; this mode has none.
  // Falls through to the shared modal markup below via the same state.
  if (standalone) {
    return (
      <div className="contacts-container contacts-container--standalone">
        {successMessage && <div className="success-toast">{successMessage}</div>}
        {errorMessage && <div className="error-toast" role="alert">{errorMessage}</div>}
        {renderModal()}
        {renderDeleteConfirm()}
      </div>
    );
  }

  return (
    <div className="contacts-container">
      <div className="contacts-header">
        <h3>Contacts ({contacts.length})</h3>
        <button className="btn-add-contact" onClick={handleAddClick}>
          + Add Contact
        </button>
      </div>

      {successMessage && <div className="success-toast">{successMessage}</div>}
      {errorMessage && <div className="error-toast" role="alert">{errorMessage}</div>}

      {contacts.length === 0 ? (
        <div className="contacts-empty">
          <p>No contacts yet for this account.</p>
          <button className="btn-add-contact-large" onClick={handleAddClick}>
            + Add First Contact
          </button>
        </div>
      ) : (
        <div className="contacts-grid">
          {contacts.map((contact) => {
            const levels = toArray(contact.influence_level);
            const primaryLevel = levels[0] || '';
            const config = getInfluenceConfig(primaryLevel);

            // Relationship → left border color
            const REL_BORDER = {
              'Strong  Relationship': '#16a34a',
              'Engaged Positve':      'var(--dm-blue-vivid)',
              'Customer':             'var(--dm-indigo)',
              'Negative':             'var(--dm-red)',
            };
            const rels = toArray(contact.relationship);
            const topRel = ['Strong  Relationship','Engaged Positve','Customer','Negative'].find(r => rels.includes(r));
            const relBorder = topRel ? REL_BORDER[topRel] : config.color;

            return (
              <div
                key={contact.id}
                className="contact-card"
                style={{ border: '0.5px solid #e5e7eb', borderLeft: '4px solid ' + relBorder }}
                onClick={() => handleCardClick(contact)}
              >
                <div className="contact-card-header">
                  <div className="contact-avatar"
                    style={topRel ? { background: relBorder + '20', color: relBorder } : {}}>
                    {(contact.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="contact-name-block">
                    <div className="contact-name">{contact.name}</div>
                    {contact.title && <div className="contact-role">{contact.title}</div>}
                  </div>
                </div>

                {/* Classification chips: Seniority · Function | Practice Area */}
                {/* Falls back to legacy department tag if classification not yet populated */}
                <div className="contact-tags">
                  {(contact.seniority || contact.function) && (
                    <span className="contact-tag contact-tag--blue">
                      {[contact.seniority, contact.function].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {contact.practice_area && contact.practice_area !== 'None' && (
                    <span className="contact-tag contact-tag--purple">{contact.practice_area}</span>
                  )}
                  {/* Fallback for unclassified contacts: show original department for context */}
                  {!contact.seniority && !contact.function && !contact.practice_area && contact.department && (
                    <span className="contact-tag contact-tag--purple" style={{ opacity: 0.6 }}>
                      {contact.department}
                    </span>
                  )}
                </div>

                {/* Influence badges */}
                {levels.length > 0 && (
                  <div className="contact-influence">
                    {levels.slice(0, 2).map((lvl, i) => {
                      const c = getInfluenceConfig(lvl);
                      return (
                        <span
                          key={i}
                          className="influence-badge"
                          style={{ backgroundColor: c.color + '20', color: c.color }}
                        >
                          {c.icon} {lvl}
                        </span>
                      );
                    })}
                    {levels.length > 2 && (
                      <span className="influence-badge" style={{ background: '#F3F4F6', color: 'var(--dm-grey-600)' }}>
                        +{levels.length - 2} more
                      </span>
                    )}
                  </div>
                )}

                {/* Relationship badges */}
                {toArray(contact.relationship).length > 0 && (
                  <div className="contact-relationships">
                    {toArray(contact.relationship).map((rel, idx) => (
                      <span
                        key={idx}
                        className={`relationship-badge relationship-${rel.toLowerCase().replace(/ /g, '-')}`}
                      >
                        {rel}
                      </span>
                    ))}
                  </div>
                )}

                <div className="contact-details">
                  {contact.email && (
                    <div className="contact-detail-row">
                      <span className="contact-detail-label">Email</span>
                      <a href={'mailto:' + contact.email} className="contact-detail-link"
                        onClick={e => e.stopPropagation()}>{contact.email}</a>
                    </div>
                  )}
                  {(contact.phone || contact.mobile_phone) && (
                    <div className="contact-detail-row">
                      <span className="contact-detail-label">Phone</span>
                      <span className="contact-detail-value">{contact.phone || contact.mobile_phone}</span>
                    </div>
                  )}
                </div>
                <div className="contact-card-footer">
                  {contact.linkedin && (
                    <a href={contact.linkedin} target="_blank" rel="noreferrer"
                      className="contact-linkedin-btn" onClick={e => e.stopPropagation()}>in</a>
                  )}
                  {!contact.zoominfo_id && !enrichedIds[contact.id] && (
                    <button className="ct-enrich-btn" onClick={e => handleEnrich(e, contact)}
                      disabled={enrichingId === contact.id} title="Enrich with ZoomInfo">
                      {enrichingId === contact.id ? '⏳' : '⚡ ZI'}
                    </button>
                  )}
                  {(contact.zoominfo_id || enrichedIds[contact.id]) && (
                    <span className="ct-enriched-badge" title="ZoomInfo enriched">✓ ZI</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {renderModal()}

      {/* ── Delete Confirmation ── */}
      {renderDeleteConfirm()}
    </div>
  );
}

export default ContactsTab;
